import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  FileCheck2,
  HeartHandshake,
  Info,
  MapPin,
  ShieldCheck,
  UserRound,
  UsersRound,
} from "lucide-react";
import Button from "../components/Button";
import { Blob, Dots, Star } from "../components/Decor";
import AccentRays from "../components/decor/AccentRays";
import HandDrawnUnderline from "../components/decor/HandDrawnUnderline";
import { mergeLocalRecord, startDemoSession } from "../lib/demoAuth";
import brandLogo from "../assets/images/logo-fix.png";
import SEO from "../components/SEO";

const steps = [
  { number: 1, label: "Akun Orang Tua", icon: UserRound },
  { number: 2, label: "Informasi Anak", icon: UsersRound },
  { number: 3, label: "Konfirmasi", icon: FileCheck2 },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  childName: "",
  childAge: "",
  targetLevel: "",
  preferredLocation: "",
  interests: "",
};

const inputClass =
  "min-h-12 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm font-medium text-ink outline-none transition placeholder:text-ink/35 focus:border-[#E7B928] focus:ring-4 focus:ring-yellow/20";

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold text-ink">{label}</span>
      {children}
      {error ? <span className="mt-1.5 block text-xs font-semibold text-coral">{error}</span> : null}
    </label>
  );
}

function Progress({ currentStep }) {
  return (
    <ol className="grid grid-cols-3" aria-label="Tahapan pendaftaran">
      {steps.map((item, index) => {
        const complete = currentStep > item.number;
        const active = currentStep === item.number;
        return (
          <li key={item.number} className="relative flex flex-col items-center text-center">
            {index > 0 ? (
              <span
                className={`absolute right-1/2 top-5 h-0.5 w-full ${currentStep > index ? "bg-yellow" : "bg-ink/12"}`}
                aria-hidden="true"
              />
            ) : null}
            <span
              className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-extrabold transition-colors ${
                complete
                  ? "border-[#E7B928] bg-yellow text-ink"
                  : active
                    ? "border-ink bg-yellow text-ink"
                    : "border-ink/15 bg-white text-ink/45"
              }`}
              aria-current={active ? "step" : undefined}
            >
              {complete ? <Check className="h-5 w-5" aria-hidden="true" /> : item.number}
            </span>
            <span className={`mt-2 text-xs font-bold sm:text-sm ${active ? "text-ink" : "text-ink/50"}`}>
              {item.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="border-b border-ink/10 py-3 last:border-0">
      <dt className="text-xs font-bold uppercase text-ink/45">{label}</dt>
      <dd className="mt-1 break-words text-sm font-semibold text-ink">{value || "Belum diisi"}</dd>
    </div>
  );
}

function BackgroundDecor() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <Dots className="absolute -left-1 top-24 h-20 w-20 opacity-25 sm:left-6" color="#73DCC6" />
      <Star className="absolute right-[6%] top-24 h-6 w-6 opacity-65" color="#FFD966" />
      <Blob className="absolute -bottom-44 -right-36 h-[420px] w-[420px] opacity-18" color="#FFD966" />
      <Blob className="absolute -left-40 top-[48%] h-80 w-80 opacity-15" color="#73DCC6" />
    </div>
  );
}

export default function ParentRegistration() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [completed, setCompleted] = useState(false);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validateStep = () => {
    const nextErrors = {};

    if (step === 1) {
      if (!form.fullName.trim()) nextErrors.fullName = "Nama lengkap perlu diisi.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Masukkan alamat email yang valid.";
      if (!form.phone.trim()) nextErrors.phone = "Nomor telepon perlu diisi.";
      if (form.password.length < 8) nextErrors.password = "Kata sandi minimal 8 karakter.";
      if (!form.confirmPassword || form.confirmPassword !== form.password) {
        nextErrors.confirmPassword = "Konfirmasi kata sandi belum sama.";
      }
    }

    if (step === 2) {
      if (!form.childName.trim()) nextErrors.childName = "Nama atau panggilan anak perlu diisi.";
      if (!form.childAge || Number(form.childAge) < 2 || Number(form.childAge) > 20) {
        nextErrors.childAge = "Masukkan usia antara 2 sampai 20 tahun.";
      }
      if (!form.targetLevel) nextErrors.targetLevel = "Pilih jenjang yang sedang dicari.";
      if (!form.preferredLocation.trim()) nextErrors.preferredLocation = "Lokasi pilihan perlu diisi.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = (event) => {
    event.preventDefault();
    if (!validateStep()) return;
    setStep((current) => Math.min(3, current + 1));
  };

  const reviewGroups = useMemo(
    () => [
      {
        title: "Akun Orang Tua",
        items: [
          ["Nama", form.fullName],
          ["Email", form.email],
          ["Nomor telepon", form.phone],
        ],
      },
      {
        title: "Informasi Anak",
        items: [
          ["Nama/Panggilan", form.childName],
          ["Usia", form.childAge ? `${form.childAge} tahun` : ""],
          ["Jenjang dicari", form.targetLevel],
          ["Lokasi pilihan", form.preferredLocation],
          ["Minat/Program", form.interests],
        ],
      },
    ],
    [form]
  );

  const finishRegistration = () => {
    if (!accepted) return;
    setCompleted(true);
    startDemoSession({ role: "parent", name: form.fullName, email: form.email });
    mergeLocalRecord("msm_parent_profile", {
      parentName: form.fullName,
      childName: form.childName,
      level: form.targetLevel,
      city: form.preferredLocation,
      interests: form.interests,
    });
    navigate("/dashboard/orang-tua", { replace: true });
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-warm px-4 py-6 sm:px-6 lg:px-8">
      <SEO title="Daftar Orang Tua | MySchoolMate" canonicalPath="/daftar/orang-tua" noIndex />
      <BackgroundDecor />

      <div className="relative z-10 mx-auto max-w-[1320px]">
        <header className="flex items-center py-2 sm:py-3">
          <Link to="/" className="rounded-lg focus-ring" aria-label="Kembali ke beranda MySchoolMate">
            <img src={brandLogo} alt="MySchoolMate" width="288" height="43" className="h-8 w-auto sm:h-9" />
          </Link>
        </header>

        <div className="mt-6 grid items-stretch gap-6 lg:grid-cols-[0.68fr_1fr] lg:gap-8 xl:gap-10">
          <aside className="relative flex flex-col overflow-hidden rounded-[30px] border border-[#E7B928]/25 bg-[#FFF9E8] p-7 sm:p-9 lg:min-h-[700px] lg:p-10">
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E7B928]/45 bg-white/80 px-3 py-2 text-xs font-extrabold uppercase text-ink">
                <HeartHandshake className="h-4 w-4 text-[#C18B00]" aria-hidden="true" />
                Orang Tua / Wali Murid
              </span>

              <div className="relative mt-7">
                <AccentRays
                  variant="curved-rays"
                  color="mint"
                  rotation={12}
                  className="absolute -right-1 -top-8 h-10 w-10 sm:right-8"
                />
                <h1 className="max-w-md text-4xl font-extrabold leading-[1.12] text-ink sm:text-5xl">
                  Daftar untuk pengalaman mencari sekolah yang lebih{" "}
                  <span className="relative inline-block">
                    terarah
                    <HandDrawnUnderline
                      variant="swoosh"
                      color="yellow"
                      className="absolute -bottom-3 left-0 h-4 w-full"
                    />
                  </span>
                </h1>
              </div>

              <p className="mt-6 max-w-md font-medium leading-relaxed text-ink/68">
                Buat akun orang tua untuk menyusun kebutuhan keluarga serta membandingkan pilihan sekolah dengan lebih mudah.
              </p>
            </div>

            <div className="relative mt-8 overflow-hidden rounded-[26px] bg-[#FFF0B7]">
              <img
                src="/assets/landing/3people.png"
                alt="Keluarga mencari informasi sekolah bersama"
                width="1024"
                height="576"
                className="h-56 w-full object-cover object-[center_40%] sm:h-64 lg:h-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-yellow/25 via-transparent to-white/5" aria-hidden="true" />
            </div>

            <div className="mt-auto pt-5">
              <div className="flex items-start gap-3 rounded-[18px] border border-[#E7B928]/30 bg-white/85 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-yellow/35 text-ink">
                  <Info className="h-5 w-5" aria-hidden="true" />
                </span>
                <p className="text-sm font-medium leading-relaxed text-ink/68">
                  <strong className="text-ink">Tips:</strong> Informasi anak pada langkah berikutnya membantu menyusun kebutuhan pencarian sekolah.
                </p>
              </div>
            </div>
          </aside>

          <section className="rounded-[30px] border border-ink/12 bg-white p-5 shadow-soft sm:p-8 lg:p-10">
            <Progress currentStep={step} />

            <div className="mt-9 border-t border-ink/10 pt-8">
              {completed ? (
                <div className="flex min-h-[480px] flex-col items-center justify-center text-center">
                  <span className="flex h-20 w-20 items-center justify-center rounded-[24px] bg-yellow/35 text-ink">
                    <CheckCircle2 className="h-10 w-10" aria-hidden="true" />
                  </span>
                  <h2 className="mt-6 text-3xl font-extrabold text-ink">Informasi siap ditindaklanjuti</h2>
                  <p className="mt-4 max-w-lg font-medium leading-relaxed text-ink/65">
                    Data sudah ditinjau di halaman ini. Pembuatan akun belum terhubung ke server pada versi saat ini.
                  </p>
                  <Button to="/signup" variant="secondary" className="mt-8 min-h-12">
                    Kembali ke pilihan peran
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </div>
              ) : null}

              {!completed && step === 1 ? (
                <form onSubmit={handleNext} noValidate>
                  <div>
                    <p className="text-xs font-extrabold uppercase text-[#C18B00]">Langkah 1 dari 3</p>
                    <h2 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">Informasi Akun Orang Tua</h2>
                    <p className="mt-2 text-sm font-medium text-ink/60">Buat akun untuk mengelola kebutuhan pencarian sekolah keluarga.</p>
                  </div>

                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Field label="Nama Lengkap" error={errors.fullName}>
                        <input name="fullName" value={form.fullName} onChange={updateField} className={inputClass} placeholder="Masukkan nama lengkap" autoComplete="name" />
                      </Field>
                    </div>
                    <Field label="Email" error={errors.email}>
                      <input name="email" type="email" value={form.email} onChange={updateField} className={inputClass} placeholder="contoh@email.com" autoComplete="email" />
                    </Field>
                    <Field label="Nomor Telepon" error={errors.phone}>
                      <input name="phone" type="tel" value={form.phone} onChange={updateField} className={inputClass} placeholder="08xxxxxxxxxx" autoComplete="tel" />
                    </Field>
                    <Field label="Kata Sandi" error={errors.password}>
                      <div className="relative">
                        <input name="password" type={showPassword ? "text" : "password"} value={form.password} onChange={updateField} className={`${inputClass} pr-12`} placeholder="Minimal 8 karakter" autoComplete="new-password" />
                        <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-ink/55 hover:bg-cream focus-ring" aria-label={showPassword ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}>
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </Field>
                    <Field label="Konfirmasi Kata Sandi" error={errors.confirmPassword}>
                      <input name="confirmPassword" type={showPassword ? "text" : "password"} value={form.confirmPassword} onChange={updateField} className={inputClass} placeholder="Ulangi kata sandi" autoComplete="new-password" />
                    </Field>
                  </div>

                  <Button type="submit" variant="yellow" className="mt-8 min-h-12 w-full">
                    Lanjutkan
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </form>
              ) : null}

              {!completed && step === 2 ? (
                <form onSubmit={handleNext} noValidate>
                  <div>
                    <p className="text-xs font-extrabold uppercase text-[#C18B00]">Langkah 2 dari 3</p>
                    <h2 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">Informasi Anak</h2>
                    <p className="mt-2 text-sm font-medium text-ink/60">Isi kebutuhan awal anak untuk membantu mengarahkan pencarian sekolah.</p>
                  </div>

                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <Field label="Nama atau Panggilan Anak" error={errors.childName}>
                      <input name="childName" value={form.childName} onChange={updateField} className={inputClass} placeholder="Masukkan nama atau panggilan" />
                    </Field>
                    <Field label="Usia Anak" error={errors.childAge}>
                      <input name="childAge" type="number" min="2" max="20" value={form.childAge} onChange={updateField} className={inputClass} placeholder="Contoh: 10" />
                    </Field>
                    <Field label="Jenjang yang Dicari" error={errors.targetLevel}>
                      <select name="targetLevel" value={form.targetLevel} onChange={updateField} className={inputClass}>
                        <option value="">Pilih jenjang</option>
                        <option value="TK">TK</option>
                        <option value="SD">SD</option>
                        <option value="SMP">SMP</option>
                        <option value="SMA">SMA</option>
                        <option value="SMK">SMK</option>
                      </select>
                    </Field>
                    <Field label="Lokasi Pilihan" error={errors.preferredLocation}>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-mint" aria-hidden="true" />
                        <input name="preferredLocation" value={form.preferredLocation} onChange={updateField} className={`${inputClass} pl-11`} placeholder="Contoh: Bandung" />
                      </div>
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Minat atau Program yang Dicari (Opsional)">
                        <textarea name="interests" value={form.interests} onChange={updateField} className={`${inputClass} min-h-[96px] resize-y`} placeholder="Contoh: Coding, tahfidz, olahraga, atau kelas kecil" />
                      </Field>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
                    <Button type="button" variant="secondary" className="min-h-12 sm:w-auto" onClick={() => setStep(1)}>
                      <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                      Kembali
                    </Button>
                    <Button type="submit" variant="yellow" className="min-h-12 flex-1">
                      Tinjau Informasi
                      <ArrowRight className="h-5 w-5" aria-hidden="true" />
                    </Button>
                  </div>
                </form>
              ) : null}

              {!completed && step === 3 ? (
                <div>
                  <div>
                    <p className="text-xs font-extrabold uppercase text-[#C18B00]">Langkah 3 dari 3</p>
                    <h2 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">Konfirmasi Informasi</h2>
                    <p className="mt-2 text-sm font-medium text-ink/60">Periksa informasi akun dan kebutuhan anak sebelum melanjutkan.</p>
                  </div>

                  <div className="mt-7 grid gap-4 md:grid-cols-2">
                    {reviewGroups.map((group) => (
                      <section key={group.title} className="rounded-[20px] border border-ink/10 bg-warm p-5">
                        <h3 className="flex items-center gap-2 font-extrabold text-ink">
                          <CheckCircle2 className="h-5 w-5 text-mint" aria-hidden="true" />
                          {group.title}
                        </h3>
                        <dl className="mt-3">
                          {group.items.map(([label, value]) => <SummaryRow key={label} label={label} value={value} />)}
                        </dl>
                      </section>
                    ))}
                  </div>

                  <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-[18px] border border-[#E7B928]/25 bg-[#FFF9E8] p-4">
                    <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#E7B928]" />
                    <span className="text-sm font-medium leading-relaxed text-ink/70">
                      Saya telah meninjau informasi di atas dan ingin melanjutkan proses pembuatan akun.
                    </span>
                  </label>

                  <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row">
                    <Button type="button" variant="secondary" className="min-h-12 sm:w-auto" onClick={() => setStep(2)}>
                      <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                      Perbaiki Data
                    </Button>
                    <Button type="button" variant="yellow" disabled={!accepted} className="min-h-12 flex-1" onClick={finishRegistration}>
                      Selesaikan Pendaftaran
                      <ArrowRight className="h-5 w-5" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>

            {!completed ? (
              <div className="mt-8 border-t border-ink/10 pt-5 text-center text-xs font-medium leading-relaxed text-ink/50">
                <div className="flex items-start justify-center gap-2">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-mint" aria-hidden="true" />
                  Informasi digunakan untuk mendukung proses pendaftaran di MySchoolMate.
                </div>
                <p className="mt-3 text-sm text-ink/60">
                  Sudah punya akun?{" "}
                  <Link to="/login" className="font-bold text-[#C18B00] underline-offset-4 hover:underline focus-ring">
                    Masuk
                  </Link>
                </p>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </main>
  );
}
